import { all, call, put, takeLatest, delay } from 'redux-saga/effects'
import ScriptActions, { ScriptTypes } from 'Redux/ScriptRedux'

// List Scripts
export function* getScripts(api, action) {
  try {
    const resp = yield call(api.getScripts, action.params)

    if (resp.ok) {
      yield put(ScriptActions.scriptsListSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to load scripts'
      yield put(ScriptActions.scriptsListFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to load scripts'
    yield put(ScriptActions.scriptsListFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Get Script Detail
export function* getScriptDetail(api, action) {
  try {
    const resp = yield call(api.getScript, action.id)

    if (resp.ok) {
      yield put(ScriptActions.scriptDetailSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to load script'
      yield put(ScriptActions.scriptDetailFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to load script'
    yield put(ScriptActions.scriptDetailFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Create Script
export function* createScript(api, action) {
  try {
    const { topic, settings } = action
    
    // Check if we need to use FormData (for character image upload)
    const hasCharacterImage = settings?.characterImage instanceof File
    
    let resp
    if (hasCharacterImage) {
      const formData = new FormData()
      formData.append('script[topic]', topic)
      formData.append('script[video_type]', settings.videoType || 'short')
      formData.append('script[aspect_ratio]', settings.aspectRatio || '9:16')
      formData.append('script[duration]', String(settings.duration || 30))
      formData.append('script[content_style]', settings.contentStyle || 'informative')
      formData.append('script[image_frequency]', String(settings.imageFrequency || 10))
      formData.append('script[subtitles]', String(settings.subtitles ?? true))
      formData.append('script[voice]', settings.voice || 'neutral')
      formData.append('script[resolution]', settings.resolution || '1080p')
      
      if (settings.imageStyle) {
        formData.append('script[image_style]', settings.imageStyle)
      }
      if (settings.themeColor) {
        formData.append('script[theme_color]', settings.themeColor)
      }
      if (settings.videoStyle) {
        formData.append('script[video_style]', settings.videoStyle)
      }
      
      formData.append('script[character_image]', settings.characterImage, settings.characterImage.name)
      formData.append('script[use_character_reference]', 'true')
      if (settings.characterLabel) {
        formData.append('script[character_label]', settings.characterLabel)
      }
      
      if (settings.animateVisuals) {
        formData.append('script[animate_visuals]', 'true')
        formData.append('script[animate_visuals_scope]', settings.animationScope || 'all')
      }
      
      if (settings.voiceId) {
        formData.append('script[voice_id]', settings.voiceId)
        if (settings.voiceName) {
          formData.append('script[voice_name]', settings.voiceName)
        }
      }
      
      resp = yield call(api.createScriptWithFormData, formData)
    } else {
      const params = {
        topic,
        video_type: settings?.videoType || 'short',
        aspect_ratio: settings?.aspectRatio || '9:16',
        duration: settings?.duration || 30,
        content_style: settings?.contentStyle || 'informative',
        image_frequency: settings?.imageFrequency || 10,
        subtitles: settings?.subtitles ?? true,
        voice: settings?.voice || 'neutral',
        resolution: settings?.resolution || '1080p',
        image_style: settings?.imageStyle || undefined,
        theme_color: settings?.themeColor || undefined,
        video_style: settings?.videoStyle || undefined,
        use_character_reference: settings?.useCharacterReference || undefined,
        character_label: settings?.characterLabel || undefined,
        animate_visuals: settings?.animateVisuals || undefined,
        animate_visuals_scope: settings?.animateVisuals ? (settings?.animationScope || 'all') : undefined,
        voice_id: settings?.voiceId || undefined,
        voice_name: settings?.voiceName || undefined,
      }
      resp = yield call(api.createScript, params)
    }

    if (resp.ok) {
      yield put(ScriptActions.scriptCreateSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to create script'
      yield put(ScriptActions.scriptCreateFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to create script'
    yield put(ScriptActions.scriptCreateFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Update Script
export function* updateScript(api, action) {
  try {
    const resp = yield call(api.updateScript, action.id, action.params)

    if (resp.ok) {
      yield put(ScriptActions.scriptUpdateSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to update script'
      yield put(ScriptActions.scriptUpdateFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to update script'
    yield put(ScriptActions.scriptUpdateFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Update Script Content
export function* updateScriptContent(api, action) {
  try {
    const resp = yield call(api.updateScript, action.id, { content: action.content })

    if (resp.ok) {
      yield put(ScriptActions.scriptUpdateContentSuccess())
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to update script content'
      yield put(ScriptActions.scriptUpdateContentFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to update script content'
    yield put(ScriptActions.scriptUpdateContentFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Update Script Step
export function* updateScriptStep(api, action) {
  try {
    const resp = yield call(api.updateScript, action.id, { current_step: action.step })

    if (resp.ok) {
      yield put(ScriptActions.scriptUpdateStepSuccess())
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to update script step'
      yield put(ScriptActions.scriptUpdateStepFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to update script step'
    yield put(ScriptActions.scriptUpdateStepFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Regenerate Script
export function* regenerateScript(api, action) {
  try {
    const resp = yield call(api.regenerateScript, action.id)

    if (resp.ok) {
      yield put(ScriptActions.scriptRegenerateSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to regenerate script'
      yield put(ScriptActions.scriptRegenerateFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to regenerate script'
    yield put(ScriptActions.scriptRegenerateFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Poll Script Status until complete
export function* pollScriptStatus(api, action) {
  const { id, callback } = action
  const interval = 3000 // 3 seconds
  const maxAttempts = 120 // 6 minutes max
  
  let attempts = 0
  
  try {
    while (attempts < maxAttempts) {
      const resp = yield call(api.getScriptStatus, id)
      
      if (!resp.ok) {
        const error = resp.data?.error || 'Failed to get script status'
        yield put(ScriptActions.scriptPollFailure(error))
        if (callback) {
          callback(null, error)
        }
        return
      }
      
      const status = resp.data
      yield put(ScriptActions.scriptPollSuccess(status))
      
      if (status.status === 'completed') {
        // Fetch full script data
        const fullResp = yield call(api.getScript, id)
        if (fullResp.ok) {
          if (callback) {
            callback(fullResp.data, null)
          }
        } else {
          if (callback) {
            callback(null, 'Failed to load completed script')
          }
        }
        return
      }
      
      if (status.status === 'failed') {
        const error = status.error_message || 'Script generation failed'
        yield put(ScriptActions.scriptPollFailure(error))
        if (callback) {
          callback(null, error)
        }
        return
      }
      
      // Continue polling - don't call callback for progress updates
      // Progress is tracked via Redux state (scriptPollSuccess)
      attempts++
      yield delay(interval)
    }
    
    // Timeout
    const error = 'Script generation timed out'
    yield put(ScriptActions.scriptPollFailure(error))
    if (callback) {
      callback(null, error)
    }
  } catch (error) {
    const errorMsg = error.message || 'Polling failed'
    yield put(ScriptActions.scriptPollFailure(errorMsg))
    if (callback) {
      callback(null, errorMsg)
    }
  }
}

// Get Surprise Script
export function* getSurpriseScript(api, action) {
  try {
    const resp = yield call(api.getSurpriseScript)

    if (resp.ok) {
      yield put(ScriptActions.scriptSurpriseSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to fetch surprise script'
      yield put(ScriptActions.scriptSurpriseFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to fetch surprise script'
    yield put(ScriptActions.scriptSurpriseFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Get Prompt Ideas
export function* getPromptIdeas(api, action) {
  try {
    const resp = yield call(api.getPromptIdeas, action.params)

    if (resp.ok) {
      yield put(ScriptActions.scriptPromptIdeasSuccess(resp.data?.ideas || []))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to fetch prompt ideas'
      yield put(ScriptActions.scriptPromptIdeasFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to fetch prompt ideas'
    yield put(ScriptActions.scriptPromptIdeasFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Generate Hooks
export function* generateHooks(api, action) {
  try {
    const resp = yield call(api.getHooks, action.params)

    if (resp.ok) {
      yield put(ScriptActions.scriptGenerateHooksSuccess(resp.data?.hooks || []))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to generate hooks'
      yield put(ScriptActions.scriptGenerateHooksFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to generate hooks'
    yield put(ScriptActions.scriptGenerateHooksFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Watcher saga
export default function* scriptSaga(api) {
  yield all([
    takeLatest(ScriptTypes.SCRIPTS_LIST_REQUEST, getScripts, api),
    takeLatest(ScriptTypes.SCRIPT_DETAIL_REQUEST, getScriptDetail, api),
    takeLatest(ScriptTypes.SCRIPT_CREATE_REQUEST, createScript, api),
    takeLatest(ScriptTypes.SCRIPT_REGENERATE_REQUEST, regenerateScript, api),
    takeLatest(ScriptTypes.SCRIPT_UPDATE_REQUEST, updateScript, api),
    takeLatest(ScriptTypes.SCRIPT_UPDATE_CONTENT_REQUEST, updateScriptContent, api),
    takeLatest(ScriptTypes.SCRIPT_UPDATE_STEP_REQUEST, updateScriptStep, api),
    takeLatest(ScriptTypes.SCRIPT_POLL_REQUEST, pollScriptStatus, api),
    takeLatest(ScriptTypes.SCRIPT_SURPRISE_REQUEST, getSurpriseScript, api),
    takeLatest(ScriptTypes.SCRIPT_PROMPT_IDEAS_REQUEST, getPromptIdeas, api),
    takeLatest(ScriptTypes.SCRIPT_GENERATE_HOOKS_REQUEST, generateHooks, api),
  ])
}
